import { Address4, Address6 } from 'ip-address'

/**
 * Helper function to validate regex pattern format
 * Value is treated as regex only if it starts with ^ and ends with $
 * @param {string} value - The value to check
 * @returns {{isRegex: boolean, isValid: boolean, errorText: string|null}} - Result of regex validation
 */
function validateRegexPattern (value) {
  const hasCaretAtStart = value.startsWith('^')
  const hasCaretAnywhere = value.includes('^')
  const hasDollarAtEnd = value.endsWith('$')
  const hasDollarAnywhere = value.includes('$')
  const dollarCount = (value.match(/\$/g) || []).length

  // Check for invalid regex patterns
  // If only ^ or only $ → Invalid
  if ((hasCaretAnywhere && !hasDollarAnywhere) || (!hasCaretAnywhere && hasDollarAnywhere)) {
    return { isRegex: true, isValid: false, errorText: 'Invalid regex expression' }
  }

  // If both exist, validate regex format
  if (hasCaretAnywhere && hasDollarAnywhere) {
    // ^ must be first
    if (!hasCaretAtStart) {
      return { isRegex: true, isValid: false, errorText: 'Invalid regex expression' }
    }

    // $ must be last
    if (!hasDollarAtEnd) {
      return { isRegex: true, isValid: false, errorText: 'Invalid regex expression' }
    }

    // Only one $ allowed
    if (dollarCount > 1) {
      return { isRegex: true, isValid: false, errorText: 'Invalid regex expression' }
    }

    if (hasCaretAtStart && hasDollarAtEnd && value.length === 2) {
      return { isRegex: true, isValid: false, errorText: 'Invalid regex expression' }
    }

    // Valid regex detected - skip character validation
    return { isRegex: true, isValid: true, errorText: null }
  }

  // No regex pattern detected
  return { isRegex: false, isValid: true, errorText: null }
}

/**
 * IP v6 network validator (with mask or not)
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const inputIPV4AndV6Validator = {
  name: 'inputIPV4AndV6Validator',
  fn: function (ip) {
    if (!ip) return true
    let validator = false

    try {
      validator = !!new Address4(ip)
    } catch {

    }

    try {
      validator = !!new Address6(ip)
    } catch {

    }
    return validator
  },
  errorText: 'Invalid IP address(-es)'
}

/**
 * Device id validator
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const deviceIdValidator = {
  name: 'deviceIdValidator',
  fn: function (deviceId) {
    if (!deviceId) return true

    return /^[A-Za-z0-9 ]+$/.test(deviceId)
  },
  errorText: 'Invalid character'
}

/**
 * Device model validator
 * Value is treated as regex only if it starts with ^ and ends with $
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const deviceModelValidator = {
  name: 'deviceModelValidator',
  fn: function (deviceModel) {
    if (!deviceModel) return true

    const regexCheck = validateRegexPattern(deviceModel)
    if (regexCheck.isRegex) {
      if (!regexCheck.isValid) {
        deviceModelValidator.errorText = regexCheck.errorText
      }
      return regexCheck.isValid
    }
    // No regex pattern - apply normal character validation
    const validator = /^[A-Za-z0-9_\-. ]+$/.test(deviceModel)
    if (!validator) {
      deviceModelValidator.errorText = 'Invalid character'
    }
    return validator
  },
  errorText: 'Invalid character'
}

/**
 * Client App Version validator
 * Value is treated as regex only if it starts with ^ and ends with $
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const clientAppVersionValidator = {
  name: 'clientAppVersionValidator',
  fn: function (clientAppVersion) {
    if (!clientAppVersion) return true

    const regexCheck = validateRegexPattern(clientAppVersion)
    if (regexCheck.isRegex) {
      if (!regexCheck.isValid) {
        clientAppVersionValidator.errorText = regexCheck.errorText
      }
      return regexCheck.isValid
    }

    // No regex pattern - apply normal character validation
    // Allowed chars: [0-9] . space
    const validator = /^[0-9. ]+$/.test(clientAppVersion)
    if (!validator) {
      clientAppVersionValidator.errorText = 'Invalid character'
    }
    return validator
  },
  errorText: 'Invalid character'
}

/**
 * User Agent validator
 * Value is treated as regex only if it starts with ^ and ends with $
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const userAgentValidator = {
  name: 'userAgentValidator',
  fn: function (userAgent) {
    if (!userAgent) return true

    const regexCheck = validateRegexPattern(userAgent)
    if (regexCheck.isRegex) {
      if (!regexCheck.isValid) {
        userAgentValidator.errorText = regexCheck.errorText
      }
      return regexCheck.isValid
    }

    // No regex pattern - apply normal character validation
    // Allowed chars: [0-9][A-Z][a-z] - _ . ; : / ( ) + space
    const validator = /^[A-Za-z0-9_\-.;:/()+ ]+$/.test(userAgent)
    if (!validator) {
      userAgentValidator.errorText = 'Invalid character'
    }
    return validator
  },
  errorText: 'Invalid character'
}

/**
 * OS Version validator
 * Value is treated as regex only if it starts with ^ and ends with $
 * @type {{errorText: string, name: string, fn: ((function(*): (boolean))|*)}}
 */
export const osVersionValidator = {
  name: 'osVersionValidator',
  fn: function (osVersion) {
    if (!osVersion) return true

    const regexCheck = validateRegexPattern(osVersion)
    if (regexCheck.isRegex) {
      if (!regexCheck.isValid) {
        osVersionValidator.errorText = regexCheck.errorText
      }
      return regexCheck.isValid
    }

    // No regex pattern - apply normal character validation
    return true
  },
  errorText: 'Invalid character'
}
